import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VStack, Box, View } from 'native-base';
import { orderBy, filter, some } from 'lodash';

import { Text } from '@pimm/base';
import { useAppLocale, useReversibleState } from '@pimm/common';
import { SiteConfigDto, UserBySiteKeyDto } from '@pimm/services/lib/sms-tenants';
import VirtualTable, { VirtualColumnProps } from '@app/components/virtual-table/virtual-table';
import { ResourceLoader } from '@app/components/shared';
import { SmsRoles, useGetUsersWithSiteKeys } from './hooks';

type UserItem = Omit<UserBySiteKeyDto, 'pinIsSet' | 'emailConfirmed'> & {
  pinIsSet?: string;
  emailConfirmed?: string;
};

type StoreUsersProps = {
  queryUsers: ReturnType<typeof useGetUsersWithSiteKeys>;
  store?: SiteConfigDto;
};

export const StoreUsers = ({ queryUsers }: StoreUsersProps) => {
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  const [tableColumns] = useReversibleState<VirtualColumnProps<UserItem>[]>(
    [
      { dataField: 'status', name: t('store_management:status') },
      { dataField: 'userName', name: t('store_management:username') },
      { dataField: 'firstName', name: t('store_management:first_name') },
      { dataField: 'lastName', name: t('store_management:last_name') },
      { dataField: 'roles', name: t('store_management:user_role') },
      { dataField: 'email', name: t('store_management:recovery_email') },
      { dataField: 'emailConfirmed', name: `${t('store_management:email_confirm')}?` },
      { dataField: 'pinIsSet', name: `${t('store_management:pin_set')}?` },
    ],
    [locale],
  );

  const tableData = useMemo(() => {
    const roles: string[] = [SmsRoles.MANAGER, SmsRoles.USER];
    const usersOnly = filter(queryUsers.data, _user =>
      some(_user.roles, _role => !!_role && roles.includes(_role.trim().toUpperCase())),
    ).map(_user => ({
      ..._user,
      pinIsSet: _user.pinIsSet ? 'Set' : undefined,
      emailConfirmed: _user.emailConfirmed ? 'Confirmed' : undefined,
    }));
    return orderBy(
      usersOnly,
      [_user => (_user.status?.toLowerCase() === 'active' ? 0 : 1), 'firstName', 'lastName'],
      ['asc', 'asc', 'asc'],
    );
  }, [queryUsers.data]);

  return (
    <VStack h="full" w="full" py={1} px={4} flex={1}>
      <Box py={2}>
        <Text size="xl" color="black" fontWeight={600}>
          {t('store_management:store_user')}
        </Text>
      </Box>

      <View w="full" flex={1}>
        <Box maxHeight="full">
          <VirtualTable
            bordered
            columns={tableColumns}
            data={tableData}
            renderEmptyData={() => {
              const isLoading = queryUsers.isFetching;
              return <ResourceLoader isEmpty isLoading={isLoading} minHeight={100} />;
            }}
          />
        </Box>
      </View>
    </VStack>
  );
};
